// Result screen. Two-column on desktop (plan preview left, therapists right); stacked on
// narrow. The empty PT state is the COMMON path (providers in ~10 states today) — it's
// designed as a good outcome, not an apology. Primary CTA is the paid gate (solid teal).
//
// Only WANT_PT ("help me find one") gets the therapist list. HAS_PT ("I already have a
// physical therapist") gets a card that says how to connect theirs later: inviting a
// therapist is a post-account action (doc 10 §13), and we deliberately never collect the
// therapist's details before the account exists (Q7b was cut for PHI reasons).
import { useEffect, useState } from 'react';
import confetti from 'canvas-confetti';
import type { Answers } from '../lib/session';
import { getProviders, type Coverage, type Provider } from '../lib/api';
import { useIsNarrow } from '../lib/useIsNarrow';
import { PrimaryButton, card } from '../components/ui';
import { stateName } from '../lib/zipState';
import { providerNoun } from '../lib/noun';
import { normalizeInsurance } from '../lib/insurance';
import { track } from '../lib/analytics';
import { HeldNotice } from '../components/HeldNotice';

const TRIAL_DAYS = Number(import.meta.env.VITE_TRIAL_DAYS || 7);

export function Result({ answers, coverage, onStart }: { answers: Answers; coverage: Coverage | null; onStart: () => void }) {
  const narrow = useIsNarrow();
  const [providers, setProviders] = useState<Provider[] | null>(null);
  const [loadingPTs, setLoadingPTs] = useState(true);
  const wantsPT = answers.intent === 'WANT_PT';
  const hasPT = answers.intent === 'HAS_PT';
  // A recent surgery we have no protocol for (the API's post-op guard): no exercise plan is
  // offered, so say so plainly instead of presenting one as ready.
  const held = coverage?.hold === 'recent-surgery';
  const st = (answers.state as string) || null;

  // A one-time reward moment — the plan is ready. Brand-teal burst, skipped for users
  // who prefer reduced motion.
  useEffect(() => {
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    // Nothing to celebrate yet when the plan is held after surgery.
    if (reduced || held) return;
    const colors = ['#21ACBE', '#17808D', '#E8F6F8', '#F2A8A8'];
    const fire = (particleRatio: number, opts: confetti.Options) =>
      confetti({ origin: { y: 0.35 }, colors, disableForReducedMotion: true, particleCount: Math.floor(160 * particleRatio), ...opts });
    fire(0.25, { spread: 26, startVelocity: 55 });
    fire(0.2, { spread: 60 });
    fire(0.35, { spread: 100, decay: 0.91, scalar: 0.8 });
    fire(0.1, { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.2 });
    fire(0.1, { spread: 120, startVelocity: 45 });
  }, [held]);

  const country = (answers.country as string) || 'US';

  useEffect(() => {
    track('landing_view'); // result view
    let live = true;
    // The in-funnel provider directory is US-only today (state-licensure filtered). UK
    // provider serving + uk-book links are a backend follow-up, so for non-US we skip the
    // query and show a country-appropriate message instead of a US "your state" empty state.
    if (!wantsPT || country !== 'US') {
      setLoadingPTs(false);
      return;
    }
    getProviders({
      state: st ?? undefined,
      insurance: (answers.insuranceProvider as string) || undefined,
      bodyPart: (answers.bodyPart as string) || undefined,
      careType: (answers.careType as string) || undefined,
    })
      .then((r) => live && setProviders(r.providers))
      .catch(() => live && setProviders([]))
      .finally(() => live && setLoadingPTs(false));
    return () => {
      live = false;
    };
  }, [wantsPT, country, st, answers.insuranceProvider, answers.bodyPart, answers.careType]);

  const planName = coverage?.planName || coverage?.interimPlanName || 'Your recovery plan';
  const goal = (answers.goal as string) || null;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--surface)' }}>
      <div style={{ maxWidth: 1080, margin: '0 auto', padding: narrow ? '28px 20px 48px' : '48px 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <svg width={16} height={16} viewBox="0 0 20 20" fill="none" aria-hidden>
            <circle cx={10} cy={10} r={10} fill="var(--teal)" />
            <path d="M5.8 10.2l2.8 2.8 5.6-5.8" stroke="#fff" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span style={{ fontSize: 11, fontWeight: 800, color: 'var(--teal-deep)', textTransform: 'uppercase', letterSpacing: '.14em' }}>
            {held ? 'Your answers are in' : 'Your plan is ready'}
          </span>
        </div>
        <div className="pretty" style={{ fontSize: narrow ? 28 : 36, fontWeight: 800, lineHeight: 1.15, marginTop: 10 }}>
          {held ? 'We’ll set up your plan with you' : coverage?.covered ? planName : 'A plan built around your recovery'}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: narrow || !(wantsPT || hasPT) ? '1fr' : '1.1fr 1fr', gap: 20, marginTop: 24, alignItems: 'start' }}>
          {/* Plan card */}
          <div style={{ ...card, borderRadius: 16, padding: 24 }}>
            {held ? (
              <HeldNotice country={country} />
            ) : (
              <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                <div style={{ background: 'var(--teal-tint)', borderRadius: 14, padding: '14px 18px', textAlign: 'center' }}>
                  <div style={{ fontSize: 30, fontWeight: 800, color: 'var(--teal-deep)', lineHeight: 1 }}>12</div>
                  <div style={{ fontSize: 10, fontWeight: 800, color: 'var(--teal-deep)', letterSpacing: '.1em' }}>WEEKS</div>
                </div>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 800 }}>{planName}</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--slate)', marginTop: 2 }}>
                    {coverage?.planType === 'SURGERY' ? 'Phase-based surgical recovery' : 'Guided phases, at your pace'}
                  </div>
                </div>
              </div>
            )}

            {goal && (
              <div style={{ background: 'var(--teal-tint)', borderRadius: 12, padding: '14px 16px', marginTop: 18 }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--teal-deep)', textTransform: 'uppercase', letterSpacing: '.12em' }}>Your goal</div>
                <div style={{ fontSize: 15, fontWeight: 700, marginTop: 4 }}>{goal}</div>
              </div>
            )}

            {coverage?.recommendBooking && wantsPT && (
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--slate)', marginTop: 16, lineHeight: 1.5 }}>
                {held ? 'We also recommend working with' : 'We recommend pairing this with'} a {providerNoun(country)}. See your matches{narrow ? ' below' : ' on the right'}.
              </div>
            )}
            {coverage?.recommendBooking && hasPT && (
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--slate)', marginTop: 16, lineHeight: 1.5 }}>
                {held ? 'We also recommend working with' : 'We recommend pairing this with'} a {providerNoun(country)}. Yours can follow along once you connect them{narrow ? ' (see below)' : ' (see the right)'}.
              </div>
            )}

            <div style={{ marginTop: 22 }}>
              <PrimaryButton full onClick={() => { track('cta_click'); onStart(); }}>
                {held ? 'Create my free account' : `Start my plan, free for ${TRIAL_DAYS} days`}
              </PrimaryButton>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--slate)', marginTop: 10, textAlign: 'center' }}>
                {held ? 'No card required.' : `No card required. Your ${TRIAL_DAYS} days start when you open the app.`}
              </div>
            </div>
          </div>

          {/* Therapist column: matches for WANT_PT; how to connect their own for HAS_PT */}
          {wantsPT && <TherapistColumn country={country} state={st} providers={providers} loading={loadingPTs} insurance={answers.insuranceProvider as string} />}
          {hasPT && <HasTherapistCard country={country} />}
        </div>

        <WhatsInside narrow={narrow} isSurgery={coverage?.planType === 'SURGERY'} />
      </div>
    </div>
  );
}

// Fills the space below the plan with what the patient actually gets — turns a sparse
// screen into a confidence-builder before they commit. Non-clinical, honest copy.
function WhatsInside({ narrow, isSurgery }: { narrow: boolean; isSurgery: boolean }) {
  const items = [
    {
      title: 'Guided daily sessions',
      body: 'Short, video-guided exercise sessions. Just a few minutes a day.',
      icon: (
        <path d="M7 5l9 5-9 5V5z" fill="var(--teal-deep)" />
      ),
    },
    {
      title: 'See your progress',
      body: isSurgery
        ? 'Track your pain and milestones through every phase of your recovery.'
        : 'Track your pain and milestones across your 12 weeks, so you can see it working.',
      icon: (
        <path d="M4 15l4-4 3 3 5-6M4 4v12h12" stroke="var(--teal-deep)" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" fill="none" />
      ),
    },
    {
      title: 'Resources to help you recover',
      body: 'Articles, tips, and answers to help you understand your recovery and stay on track.',
      icon: (
        <>
          <path d="M6 3h5l3 3v11H6V3z" stroke="var(--teal-deep)" strokeWidth={1.6} fill="none" strokeLinejoin="round" />
          <path d="M11 3v3h3" stroke="var(--teal-deep)" strokeWidth={1.6} fill="none" strokeLinejoin="round" />
          <path d="M8.5 11h3M8.5 13.5h3" stroke="var(--teal-deep)" strokeWidth={1.6} strokeLinecap="round" />
        </>
      ),
    },
  ];
  return (
    <div style={{ marginTop: 28 }}>
      <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--teal-deep)', textTransform: 'uppercase', letterSpacing: '.12em', marginBottom: 14 }}>
        What’s inside your plan
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: narrow ? '1fr' : 'repeat(3, 1fr)', gap: 14 }}>
        {items.map((it) => (
          <div key={it.title} style={{ ...card, borderRadius: 16, padding: 20 }}>
            <div style={{ width: 40, height: 40, borderRadius: 12, background: 'var(--teal-tint)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
              <svg width={20} height={20} viewBox="0 0 20 20">{it.icon}</svg>
            </div>
            <div style={{ fontSize: 15, fontWeight: 800 }}>{it.title}</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--slate)', marginTop: 6, lineHeight: 1.5 }}>{it.body}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Where a therapist joins Expy (the public provider application) and where a patient
// reaches us once they have an account. Absolute: the widget is also embedded in an
// iframe on expyhealth.com, where a relative link would point at the wrong site.
const PROVIDER_APPLY_URL = 'https://start.expyhealth.com/providers/apply';
const DIRECTORY_URL = 'https://expyhealth.com/physical-therapists/';
const SUPPORT_URL = 'https://expyhealth.com/support';

// The HAS_PT card. No form: we never take the therapist's details before the account
// exists. Says what the patient can do later, honestly (a person at Expy makes the link).
function HasTherapistCard({ country }: { country: string }) {
  const noun = providerNoun(country);
  const steps = [
    <>Ask your {noun} to join Expy at <a href={PROVIDER_APPLY_URL} target="_blank" rel="noreferrer" style={{ color: 'var(--teal-deep)', fontWeight: 800 }}>start.expyhealth.com/providers/apply</a>.{country === 'US' ? <> If they are already listed in <a href={DIRECTORY_URL} target="_blank" rel="noreferrer" style={{ color: 'var(--teal-deep)', fontWeight: 800 }}>our directory</a>, they can claim their listing instead.</> : null}</>,
    <>Once you are in the app, tell us who they are through Help (or <a href={SUPPORT_URL} target="_blank" rel="noreferrer" style={{ color: 'var(--teal-deep)', fontWeight: 800 }}>expyhealth.com/support</a>). We link them to your plan so they can follow your progress and message you.</>,
  ];
  return (
    <div style={{ ...card, borderRadius: 16, padding: 24 }}>
      <div style={{ fontSize: 17, fontWeight: 800 }}>You already have a {noun}</div>
      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--slate)', marginTop: 8, lineHeight: 1.55 }}>
        Your plan works alongside their care from day one. We do not ask for your {noun}’s details here. Connecting them happens after your account exists, from inside the app.
      </div>
      <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--teal-deep)', textTransform: 'uppercase', letterSpacing: '.12em', marginTop: 18 }}>
        How to connect them later
      </div>
      <ol style={{ margin: '10px 0 0', paddingLeft: 20, fontSize: 14, fontWeight: 600, color: 'var(--slate)', lineHeight: 1.55 }}>
        {steps.map((step, i) => (
          <li key={i} style={{ marginTop: i ? 8 : 0 }}>{step}</li>
        ))}
      </ol>
      <div style={{ background: 'var(--teal-tint)', borderRadius: 12, padding: '14px 16px', marginTop: 16, fontSize: 13, fontWeight: 700, color: 'var(--teal-deep)' }}>
        Nothing to do now. Start your plan; your {noun} can join whenever they are ready.
      </div>
    </div>
  );
}

function TherapistColumn({ country, state, providers, loading, insurance }: { country: string; state: string | null; providers: Provider[] | null; loading: boolean; insurance?: string }) {
  const stName = stateName(state) || 'your area';
  if (loading) {
    return <div style={{ ...card, borderRadius: 16, padding: 24, fontSize: 14, fontWeight: 600, color: 'var(--slate)' }}>Finding {providerNoun(country, true)}…</div>;
  }
  // The funnel doesn't fetch providers for non-US markets (the in-funnel directory is
  // state-licensure filtered, US-only). The UK is live: a small roster of HCPC-registered
  // physiotherapists, each covering the whole country, matched by concierge — so promise
  // the active match (fast, no "near you") and point at the UK directory to book directly.
  // Anywhere else has no provider/booking yet, so be honest that matching isn't available.
  if (country === 'GB') {
    return (
      <div style={{ ...card, borderRadius: 16, padding: 24 }}>
        <div style={{ fontSize: 17, fontWeight: 800 }}>We’re finding your physiotherapist</div>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--slate)', marginTop: 8, lineHeight: 1.55 }}>
          Your plan works on its own from day one. Expy matches you with an HCPC-registered physiotherapist who treats what you’re dealing with, and gets back to you shortly. They can see you anywhere in the UK, by video.
        </div>
        <div style={{ background: 'var(--teal-tint)', borderRadius: 12, padding: '14px 16px', marginTop: 16, fontSize: 13, fontWeight: 700, color: 'var(--teal-deep)' }}>
          Start your plan now. You can also <a href="https://expyhealth.com/uk/physiotherapists/" target="_blank" rel="noreferrer" style={{ color: 'var(--teal-deep)', fontWeight: 800, textDecoration: 'underline' }}>browse and book our physiotherapists</a> yourself.
        </div>
      </div>
    );
  }
  if (country !== 'US') {
    return (
      <div style={{ ...card, borderRadius: 16, padding: 24 }}>
        <div style={{ fontSize: 17, fontWeight: 800 }}>Physical therapists aren’t in your region yet</div>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--slate)', marginTop: 8, lineHeight: 1.55 }}>
          We only connect physical therapists and physiotherapists in the US and UK for now. Your plan works anywhere, though, so start it today and we’ll let you know if that changes where you are.
        </div>
      </div>
    );
  }
  const list = providers || [];
  if (list.length === 0) {
    // Honest empty state: Expy runs a concierge search (doc 10). Say what
    // actually happens (we look and get back to you), not "we'll email you the
    // moment a PT joins", which nothing implements. The directory hub is the floor.
    const stFull = stateName(state);
    const hubUrl = stFull
      ? `${DIRECTORY_URL}${stFull.toLowerCase().replace(/[^a-z]+/g, '-').replace(/(^-|-$)/g, '')}/`
      : DIRECTORY_URL;
    return (
      <div style={{ ...card, borderRadius: 16, padding: 24 }}>
        <div style={{ fontSize: 17, fontWeight: 800 }}>We’re finding you a physical therapist in {stName}</div>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--slate)', marginTop: 8, lineHeight: 1.55 }}>
          Your plan works on its own from day one. Expy looks for a licensed physical therapist in {stName} who takes your plan, and gets back to you in the next few days.
        </div>
        <div style={{ background: 'var(--teal-tint)', borderRadius: 12, padding: '14px 16px', marginTop: 16, fontSize: 13, fontWeight: 700, color: 'var(--teal-deep)' }}>
          Start your plan now. You can also <a href={hubUrl} target="_blank" rel="noreferrer" style={{ color: 'var(--teal-deep)', fontWeight: 800, textDecoration: 'underline' }}>browse physical therapists in {stName}</a> yourself.
        </div>
      </div>
    );
  }
  return (
    <div>
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--slate)', marginBottom: 10 }}>
        {insurance ? `Ranked for your ${insurance} plan · ` : ''}{list.length} licensed in {stName}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {list.map((p) => (
          <ProviderCard key={p.id} p={p} insurance={insurance} />
        ))}
      </div>
    </div>
  );
}

function ProviderCard({ p, insurance }: { p: Provider; insurance?: string }) {
  // Normalize both sides so the badge is right even if provider values aren't yet
  // normalized-at-import (e.g. "Anthem" on the record vs "Blue Cross Blue Shield" picked).
  const wantedCanonical = normalizeInsurance(insurance) || insurance?.toLowerCase();
  const takesInsurance = insurance
    ? p.acceptedInsurancePlans.some((x) => (normalizeInsurance(x) || x.toLowerCase()) === wantedCanonical)
    : false;
  const initials = p.fullName.split(/\s+/).map((s) => s[0]).slice(0, 2).join('').toUpperCase();
  return (
    <div style={{ ...card, borderRadius: 14, padding: 18 }}>
      <div style={{ display: 'flex', gap: 14 }}>
        <div style={{ width: 44, height: 44, borderRadius: 12, background: 'var(--teal-tint)', color: 'var(--teal-deep)', fontWeight: 800, fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          {initials}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 16, fontWeight: 800 }}>{p.fullName}</div>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--slate)', marginTop: 1 }}>
            {[p.yearsInPractice ? `${p.yearsInPractice} yrs` : null, p.city].filter(Boolean).join(' · ')}
          </div>
          <div style={{ fontSize: 12, fontWeight: 800, color: takesInsurance ? 'var(--teal-deep)' : 'var(--slate)', marginTop: 6, letterSpacing: '.04em' }}>
            {insurance ? (takesInsurance ? `TAKES ${insurance.toUpperCase()}` : `Cash rates · doesn’t take ${insurance}`) : ''}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
            {p.specialties.slice(0, 3).map((s) => (
              <span key={s} style={{ fontSize: 11, fontWeight: 700, color: 'var(--slate)', background: '#F1F4F5', borderRadius: 6, padding: '3px 8px' }}>{s}</span>
            ))}
          </div>
        </div>
      </div>
      <a
        href={p.bookingUrl || '#'}
        target="_blank"
        rel="noreferrer"
        style={{ display: 'block', textAlign: 'center', marginTop: 14, border: '1.5px solid var(--teal)', color: 'var(--teal-deep)', fontWeight: 800, fontSize: 14, borderRadius: 10, padding: '11px' }}
      >
        {p.isBookable ? 'See availability' : 'Book intro call'}
      </a>
    </div>
  );
}
